import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const caseStudies = [
  {
    industry: "MED SPA",
    bg: "bg-black",
    problem:
      "Missed 40% of consultation requests during business hours. Front desk couldn't handle inquiry volume from paid ads, organic social, and walk-ins simultaneously.",
    system: {
      name: "AI PATIENT ACQUISITION + FOLLOW-UP SYSTEM",
      bullets: [
        "AI call and message handling across phone, SMS, and web chat",
        "Instant booking link delivery with personalized follow-up",
        "Automated 5-step nurture sequence for non-bookers",
      ],
    },
    results: [
      { metric: "+42%", label: "BOOKED APPOINTMENTS" },
      { metric: "3x", label: "FASTER RESPONSE TIME" },
      { metric: "+28%", label: "RETENTION RATE" },
    ],
  },
  {
    industry: "CLINICAL PRACTICE",
    bg: "bg-[#1A1A1A]",
    problem:
      "Patient intake consumed 6 hours of staff time daily. Manual scheduling, paper-based intake, and phone-tag follow-up made scaling to new locations operationally impossible.",
    system: {
      name: "CLINICAL AUTOMATION INFRASTRUCTURE",
      bullets: [
        "Digital intake and eligibility verification workflow",
        "Compliance-ready automated scheduling with smart reminders",
        "Referral coordination and care handoff automation",
      ],
    },
    results: [
      { metric: "-67%", label: "ADMIN TIME ELIMINATED" },
      { metric: "94%", label: "SCHEDULING ACCURACY" },
      { metric: "+51%", label: "PATIENT CAPACITY INCREASE" },
    ],
  },
  {
    industry: "WELLNESS BRAND",
    bg: "bg-black",
    problem:
      "Single-purchase customers represented 73% of total revenue loss. Post-visit communication was nonexistent—no follow-up, no rebooking prompts, no loyalty structure.",
    system: {
      name: "LIFECYCLE RETENTION SYSTEM",
      bullets: [
        "Automated post-visit journey with personalized rebooking nudges",
        "Recurring booking triggers at optimal service intervals",
        "Churn prediction alerts with automated re-engagement campaigns",
      ],
    },
    results: [
      { metric: "+89%", label: "REPEAT BOOKING RATE" },
      { metric: "3.4x", label: "CUSTOMER LIFETIME VALUE" },
      { metric: "81%", label: "12-MONTH RETENTION" },
    ],
  },
];

const aggregateMetrics = [
  { value: "2.8x", label: "AVG CONVERSION LIFT" },
  { value: "47+", label: "ACTIVE CLIENT SYSTEMS" },
  { value: "-58%", label: "AVG ADMIN REDUCTION" },
  { value: "+73%", label: "AVG RETENTION GROWTH" },
];

const processSteps = [
  {
    number: "01",
    name: "AUDIT",
    desc: "We map your current patient acquisition and retention workflows to identify all system gaps, revenue leaks, and automation opportunities.",
  },
  {
    number: "02",
    name: "BUILD",
    desc: "Custom automation infrastructure built to your operational model, compliance requirements, and growth objectives.",
  },
  {
    number: "03",
    name: "DEPLOY",
    desc: "Systems go live within 14 days. Full team training and handoff documentation included. Zero operational disruption.",
  },
  {
    number: "04",
    name: "OPTIMIZE",
    desc: "Continuous performance monitoring with monthly optimization sprints to increase conversion rates and retention metrics.",
  },
];

const CaseStudiesPage = () => {
  return (
    <div className="bg-black min-h-screen" style={{ fontFamily: "Arial, sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-black min-h-[560px] flex flex-col items-center justify-center overflow-hidden pt-20">
        {/* Grid bg */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.06]">
            <defs>
              <pattern id="csGrid" width="80" height="80" patternUnits="userSpaceOnUse">
                <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#FFD100" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#csGrid)" />
          </svg>
        </div>

        <div className="relative z-10 text-center px-6 md:px-16 max-w-5xl mx-auto py-24">
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="block w-12 h-px bg-[#FFD100]" />
            <span className="text-[#FFD100] text-xs tracking-[4px] font-bold uppercase">CASE STUDIES</span>
            <span className="block w-12 h-px bg-[#FFD100]" />
          </div>
          <h1
            className="text-white font-black uppercase leading-none mb-8"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "clamp(2.5rem, 5.5vw, 5rem)",
              letterSpacing: "-1px",
              lineHeight: "1.05",
            }}
          >
            SYSTEMS THAT
            <br />
            <span className="text-[#FFD100]">DELIVER RESULTS</span>
          </h1>
          <p
            className="text-[#E5E5E5] text-base md:text-lg max-w-xl mx-auto leading-relaxed"
            style={{ lineHeight: "1.7" }}
          >
            We don&apos;t install tools—we build performance infrastructure.
          </p>
        </div>
      </section>

      {/* Case Study Cards */}
      <section className="py-8 md:py-12 px-6 md:px-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
          {caseStudies.map((cs, i) => (
            <div
              key={i}
              className={`${cs.bg} border border-white/10 p-8 md:p-12 relative overflow-hidden`}
            >
              {/* Right gold accent line */}
              <div className="absolute right-0 top-[15%] bottom-[15%] w-0.5 bg-[#FFD100]/30" />

              {/* Top row */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-10">
                <span
                  className="text-[#FFD100] text-xs font-black tracking-[3px] border border-[#FFD100]/40 px-4 py-2 self-start"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  {cs.industry}
                </span>
                <p
                  className="text-white font-bold text-lg md:text-xl leading-snug max-w-lg sm:text-right"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  &ldquo;{cs.problem}&rdquo;
                </p>
              </div>

              {/* System */}
              <div className="mb-10">
                <span
                  className="text-[#E5E5E5] text-xs tracking-[3px] font-bold block mb-4"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  SYSTEM DEPLOYED: {cs.system.name}
                </span>
                <ul className="flex flex-col gap-2">
                  {cs.system.bullets.map((b, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-[#E5E5E5] text-sm"
                      style={{ lineHeight: "1.7" }}
                    >
                      <span className="text-[#FFD100] font-bold flex-shrink-0">—</span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Results */}
              <div className="border-t border-[#FFD100]/20 pt-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
                {cs.results.map((r, j) => (
                  <div key={j} className="flex flex-col gap-2">
                    <div className="w-12 h-0.5 bg-[#FFD100] mb-2" />
                    <div
                      className="text-[#FFD100] font-black leading-none"
                      style={{
                        fontFamily: "Arial, sans-serif",
                        fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                      }}
                    >
                      {r.metric}
                    </div>
                    <span
                      className="text-white font-bold text-sm tracking-[2px]"
                      style={{ fontFamily: "Arial, sans-serif" }}
                    >
                      {r.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Aggregate Performance */}
      <section className="bg-[#1A1A1A] py-24 md:py-32 px-6 md:px-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-4">
              AGGREGATE PERFORMANCE
            </span>
            <h2
              className="text-white font-black uppercase text-3xl md:text-5xl"
              style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}
            >
              IMPACT ACROSS ALL CLIENTS
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#FFD100]/10">
            {aggregateMetrics.map((m, i) => (
              <div
                key={i}
                className="bg-black p-8 md:p-12 flex flex-col items-center justify-center text-center gap-4"
              >
                <div
                  className="text-[#FFD100] font-black leading-none"
                  style={{
                    fontFamily: "Arial, sans-serif",
                    fontSize: "clamp(3rem, 6vw, 5rem)",
                  }}
                >
                  {m.value}
                </div>
                <span
                  className="text-white font-bold text-xs tracking-[2px]"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  {m.label}
                </span>
              </div>
            ))}
          </div>

          <p
            className="text-center text-[#E5E5E5]/50 text-xs tracking-[2px] mt-8"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            PERFORMANCE DATA AGGREGATED ACROSS 47 ACTIVE CLIENT SYSTEMS · UPDATED Q1 2026
          </p>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-black py-24 md:py-32 px-6 md:px-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-4">BUILD PROCESS</span>
            <h2
              className="text-white font-black uppercase text-4xl md:text-5xl"
              style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}
            >
              HOW WE WORK
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
            {processSteps.map((step, i) => (
              <div
                key={i}
                className="p-8 border-t-2 border-[#FFD100] border-r border-white/5 last:border-r-0 bg-[#1A1A1A] relative"
              >
                {/* Step number */}
                <div
                  className="text-[#FFD100] font-black leading-none mb-6"
                  style={{ fontFamily: "Arial, sans-serif", fontSize: "4rem", opacity: 0.9 }}
                >
                  {step.number}
                </div>

                {/* Step name */}
                <h3
                  className="text-white font-black text-xl tracking-[2px] mb-4"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  {step.name}
                </h3>

                {/* Description */}
                <p
                  className="text-[#E5E5E5] text-sm leading-relaxed"
                  style={{ lineHeight: "1.8" }}
                >
                  {step.desc}
                </p>

                {/* Arrow connector */}
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10">
                    <div className="w-6 h-6 flex items-center justify-center bg-black">
                      <i className="ri-arrow-right-s-line text-[#FFD100] text-lg" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] py-24 md:py-36 px-6 md:px-16 border-t border-white/5 relative overflow-hidden">
        {/* Vertical gold lines */}
        <div className="hidden md:block absolute left-1/3 top-0 bottom-0 w-px bg-[#FFD100]/10 pointer-events-none" />
        <div className="hidden md:block absolute right-1/3 top-0 bottom-0 w-px bg-[#FFD100]/10 pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-6 mb-10">
            <div className="w-20 md:w-32 h-px bg-[#FFD100]/40" />
            <div className="w-2 h-2 rotate-45 bg-[#FFD100] flex-shrink-0" />
            <div className="w-20 md:w-32 h-px bg-[#FFD100]/40" />
          </div>

          <h2
            className="text-white font-black uppercase leading-tight mb-8"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "clamp(1.8rem, 4vw, 3.5rem)",
              letterSpacing: "-0.5px",
              lineHeight: "1.1",
            }}
          >
            RESULTS ARE BUILT,
            <br />
            <span className="text-[#FFD100]">NOT HOPED FOR.</span>
          </h2>

          <p className="text-[#E5E5E5] text-sm max-w-lg mx-auto mb-12 leading-relaxed" style={{ lineHeight: "1.8" }}>
            Stop guessing and start building. Every result in this page started with a single audit call.
          </p>

          <Link
            to="/contact"
            className="bg-[#FFD100] text-black text-xs font-black tracking-[3px] px-12 py-5 cursor-pointer whitespace-nowrap hover:bg-white transition-all duration-200 inline-block"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            START YOUR SYSTEM BUILD
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudiesPage;
